import {useORingCalculatorStore} from '@/store/oring-calculator'
import {useOringCalculatorService} from '@/composables/oring_calc_service'
import useI18nExtended from '~/composables/usei18nExtended'
import {useGtm} from '@gtm-support/vue-gtm'

/**
 * Get SEO options for O-Ring Calculator pages
 * @param {string} path - Optional path suffix for the URL
 * @param {string} translationKey - Translation key for the page title (defaults to 'Frontend.ORing.Title')
 * @param {string} translationEntity - Translation namespace for the title key (defaults to 'ORingConfiguratorImport')
 * @returns {object} - Object with SEO metadata
 */
export function getORingSeoOptions(path = '', translationKey = 'Frontend.ORing.Title', translationEntity = 'ORingConfiguratorImport') {
  const i18n = useI18nExtended()
  const {locale} = useNuxtApp().$i18n
  const region = useNuxtApp().$globalization.getRegion().toLowerCase()
  const seoPath = path ? `/${path}` : ''
  const BASE_URL = useRuntimeConfig().public.BASE_URL

  return {
    title: i18n.tv(translationKey, translationEntity),
    description: i18n.tv('ogDescription', 'ORingConfigurator'),
    image: '/img/oring/og_image.jpg',
    url: `${BASE_URL}/${region}/${locale.value}/oring-calculator${seoPath}`,
    locale: locale.value
  }
}

/**
 * Handles the construction submit logic for O-Ring Design Cockpit and Assistant
 * @param {object} params - Parameters for the submit
 * @param {'download'|'send'} params.mode - The action mode
 * @param {any} params.popupData - Data from the popup (form)
 * @param {'cockpit'|'assistant'} params.housingType - The housing type
 * @param {any} params.customHousingData - Optional custom housing data to override store data
 * @returns {Promise<any>} - The result of the API call
 */
export async function handleConstructionSubmit({mode, popupData, housingType = 'cockpit', customHousingData}: {
  mode: 'download' | 'send',
  popupData: {
    name: string,
    company: string,
    email: string,
    designRemarks: string,
    pdfLanguage: string | { label: string, value: string }
  },
  housingType?: 'cockpit' | 'assistant' | 'designSpace',
  customHousingData?: any
}) {
  const oringStore = useORingCalculatorStore()
  const service = useOringCalculatorService()
  try {
    const languageValue = typeof popupData.pdfLanguage === 'string'
      ? popupData.pdfLanguage
      : (popupData.pdfLanguage?.value || 'en')

    const pdfDownloadParameter = {
      name: popupData.name,
      company: popupData.company,
      designRemark: popupData.designRemarks,
      eMail: popupData.email
    }
    const oringConfigDownloadPdfReq = {
      pdfDownloadParameter,
      language: languageValue,
      imperial: oringStore.oringCalculatorImperial,
      creationTimeIso: '',
      pdfHousing: null
    }

    oringStore.oringPdfData = oringConfigDownloadPdfReq

    let pdfRequest, mailRequest, pdfHousing
    if (housingType === 'assistant') {
      const basePdfRequest = oringStore.oringAssistantDownloadPdfRequest()
      const baseMailRequest = oringStore.oringAssistantSendMailRequest()

      pdfRequest = {
        ...basePdfRequest,
        pdfHousing: customHousingData || basePdfRequest.pdfHousing
      }
      mailRequest = {
        ...baseMailRequest,
        pdfHousing: customHousingData || baseMailRequest.pdfHousing
      }
      pdfHousing = pdfRequest.pdfHousing
    } else if (housingType === 'designSpace') {
      const basePdfRequest = oringStore.oringDesignSpaceDownloadPdfRequest()
      const baseMailRequest = oringStore.oringDesignSpaceSendMailRequest()

      pdfRequest = {
        ...basePdfRequest,
        pdfHousing: customHousingData || basePdfRequest.pdfHousing
      }
      mailRequest = {
        ...baseMailRequest,
        pdfHousing: customHousingData || baseMailRequest.pdfHousing
      }
      pdfHousing = pdfRequest.pdfHousing
    } else {
      const basePdfRequest = oringStore.oringDesignCockpitDownloadPdfRequest()
      const baseMailRequest = oringStore.oringDesignCockpitSendMailRequest()

      pdfRequest = {
        ...basePdfRequest,
        pdfDownloadParameter: pdfDownloadParameter
      }
      mailRequest = {
        ...baseMailRequest
      }
      pdfHousing = pdfRequest.pdfHousing
    }

    if (mode === 'download') {
      const pdfData = await service.downloadPdfORingConfigurator(pdfRequest)
      if (typeof window !== 'undefined' && pdfData) {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(pdfData)
        const d1Value = pdfHousing?.oRing?.d1?.val.toFixed(2) || ''
        const d2Value = pdfHousing?.oRing?.d2?.val.toFixed(2) || ''
        link.download = `o-ring_${d1Value}x${d2Value}.pdf`
        link.click()
        URL.revokeObjectURL(link.href)

        useGtm()?.trackEvent({event: 'oring_downloadconstruction'})
      }
    } else if (mode === 'send') {
      await service.sendMailORingConfigurator(mailRequest)

      useGtm()?.trackEvent({event: 'oring_sendconstruction'})
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error processing your request', error)
    throw error
  }
}
